// ──────────────────────────────────────────────────────────────────────────
// Shared help-text types for Jarvis help panels.
// ──────────────────────────────────────────────────────────────────────────
//
// Two domains currently use these types:
//   - trustPortalHelpText.ts  — Trust Portal screens (Admin → Security → Trust Portal)
//   - adminSecurityHelpText.ts — Admin → Security/Code sub-tab screens
//
// The HelpPanel component renders any HelpEntry the same way, so adding a
// new domain in the future only requires a new data file + optional wrapper
// component. The component does NOT hardcode any domain-specific lookup.

/**
 * One help entry — describes a single screen in the four-part structure
 * that the help panel renders. `relatedLinks` is optional and, when
 * provided, renders a "Related" section at the bottom of the panel with
 * clickable links that can navigate the user to a different screen.
 */
export interface HelpEntry {
  title: string;
  whatItDoes: string;
  howToNavigate: string;
  whatItContains: string;
  keyActions: string;
  relatedLinks?: HelpLink[];
}

/**
 * One related link — rendered as a clickable item at the bottom of the
 * help panel. When clicked, a global `jarvis-admin-navigate` custom event
 * is dispatched on `window` with the target tab payload; Admin.tsx
 * listens for this event and updates its internal state to switch the
 * user to the target screen.
 *
 * This indirection avoids prop-drilling navigation state through every
 * screen that renders a help icon — the help panel doesn't need to know
 * anything about Admin.tsx's state shape.
 */
export interface HelpLink {
  label: string;
  description?: string;
  // Top-level Admin tab (e.g. 'security-code', 'users-roles')
  targetTopTab?: string;
  // Security/Code sub-tab (e.g. 'sso-logins', 'evidence-collection')
  targetSubTab?: string;
  // Evidence Collection sub-sub-tab (e.g. 'compliance-drift', 'ai-assistant')
  targetSubSubTab?: string;
}

/**
 * The payload shape dispatched on `window` via the
 * `jarvis-admin-navigate` custom event. Admin.tsx listens for this
 * event and uses the payload to update its state.
 */
export interface AdminNavigatePayload {
  targetTopTab?: string;
  targetSubTab?: string;
  targetSubSubTab?: string;
}

export const ADMIN_NAVIGATE_EVENT = 'jarvis-admin-navigate';
